import { Breadcrumbs, SlideComponent } from 'components/Shared';
import FlexBox from 'components/Shared/Container/FlexBox';
import ListItem from 'components/Shared/ListItem';
import RawMaterialFilter from 'components/Shared/RawMaterialFilter/RawMaterialFilter';
import isNull from 'lodash/isNull';
import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PinTarget } from 'types/types';
import { breadcrumbUtils } from 'utils';
import { ActivityContainer } from '../Activities';
import { useActiveContext } from '../Context/ActiveContext';
import { ProductJourneyPages } from '../pages';
import SummaryChip from './SummaryChip';
import { ButtonOutlinedPrimary } from 'components/Shared/Button';
import { X as Close } from '@styled-icons/bootstrap/X';
import { CoordinateFeature } from 'types/site.types';
const PADDING_SCROLL_CONTAINER = 32;
const HeaderContainer = styled.div `
  position: relative;
  z-index: 1;
  padding: 32px 16px 24px 0;
  background: #fff;

  ${({ scrolled }) => scrolled &&
    `
    box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.05);
  `}
`;
const ScrollContainer = styled.div `
  padding: ${({ padding }) => `10px ${padding}px 0 ${padding}px`};
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  // Subtract the height of the header
  height: calc(100% - 94px);
`;
const Description = styled.span `
  color: #3b3b3b;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18.2px;
  margin-bottom: 16px !important;
`;
const Title = styled.h3 `
  font-family: Lato;
  font-size: 16px !important;
  font-style: normal;
  font-weight: 900;
  line-height: 20px; /* 125% */
  letter-spacing: -0.32px;
`;
const ButtonContainer = styled.div `
  width: fit-content;
  margin-top: ${({ marginTop }) => (marginTop ? `${marginTop}px` : '0')};
`;
const ExitButton = styled(ButtonOutlinedPrimary) `
  text-transform: none !important;
`;
const DesktopActivitySlideWrapper = ({ children }) => {
    const { t } = useTranslation();
    const scrollRef = useRef();
    const [scrollTo, setScrollTo] = useState(null);
    const [scroll, setScroll] = useState(0);
    useEffect(() => {
        if (!(scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.current) || isNull(scrollTo))
            return;
        scrollRef.current.scrollTo({
            top: scrollTo - 60,
            behavior: 'smooth',
        });
    }, [scrollTo]);
    useEffect(() => {
        if (!(scrollRef === null || scrollRef === void 0 ? void 0 : scrollRef.current))
            return;
        const handleScroll = throttle(() => {
            setScroll(scrollRef.current.scrollTop);
            setScrollTo(null);
        }, 250);
        scrollRef.current.addEventListener('scroll', handleScroll);
        return () => { var _a; return (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', handleScroll); };
    }, [scrollRef.current]);
    return (React.createElement(SlideComponent, null,
        React.createElement(HeaderContainer, { scrolled: scroll > 0 },
            React.createElement(Breadcrumbs, { config: [
                    breadcrumbUtils.getBreadcrumb(ProductJourneyPages.PRODUCT_JOURNEY, t),
                ] })),
        React.createElement(ScrollContainer, { padding: PADDING_SCROLL_CONTAINER, "data-cy": "desktop-node-slide", ref: scrollRef }, children)));
};
const DesktopActivitySlide = ({ onImpactClaimClick, onActivitySitesClick, }) => {
    const { activeStepIndex, activeStepActivity, activeStep, allRawMaterials, selectedRawMaterialId, showRawMaterialFilter, initialOriginSelection, initialRawMaterialSelection, isBatchTracingActive, summary, allSelectedStepActivityRawMaterialIds, handleStopBatchTracingClick, handleSelectedRawMaterialChange, handleSelectedActivityChange, handleAnimatedPinChange, onActiveSiteChange, } = useActiveContext();
    const { t } = useTranslation();
    const handleClickNode = (id) => {
        handleSelectedActivityChange(id);
        handleAnimatedPinChange({ id: id, target: PinTarget.MAP });
    };
    if (initialRawMaterialSelection) {
        return (React.createElement(DesktopActivitySlideWrapper, null,
            React.createElement(Description, null, t('product-journey-description')),
            React.createElement(SummaryChip, { summary: summary }),
            React.createElement(FlexBox, { alignItems: "flex-start", gap: 1, flexDirection: "column", mb: 2, mt: 3 },
                React.createElement(Title, null, t('select-ingredient-view')),
                React.createElement(FlexBox, { gap: 1, flexDirection: "column", width: "100%" },
                    React.createElement(ListItem, { title: t('all-ingredients'), onClick: () => handleSelectedRawMaterialChange(undefined) }),
                    allRawMaterials.map(({ id, title, imageUrl }) => (React.createElement(ListItem, { key: id, imageUrl: imageUrl, title: title, onClick: () => handleSelectedRawMaterialChange(id) })))))));
    }
    if (initialOriginSelection) {
        return (React.createElement(DesktopActivitySlideWrapper, null,
            showRawMaterialFilter && (React.createElement(RawMaterialFilter, { variant: isBatchTracingActive ? 'primary' : 'default', items: allRawMaterials, selectedId: selectedRawMaterialId, hideAllSelection: isBatchTracingActive, highlightedIds: [], onSelectionChange: handleSelectedRawMaterialChange })),
            React.createElement(SummaryChip, { summary: summary }),
            React.createElement(FlexBox, { alignItems: "flex-start", gap: 1, flexDirection: "column", mb: 2, mt: 3 },
                React.createElement(Title, null, t('select-origin-view')),
                React.createElement(FlexBox, { gap: 1, flexDirection: "column", width: "100%" }, activeStep.chainStepActivities.map(({ id, activity: { component, site: { locationName, coordinateFeatures }, }, }) => {
                    var _a;
                    return (React.createElement(ListItem, { key: id, tag: component === null || component === void 0 ? void 0 : component.title, iconName: "pin-selected", title: ((_a = coordinateFeatures === null || coordinateFeatures === void 0 ? void 0 : coordinateFeatures.find(({ featureType }) => CoordinateFeature.COUNTRY === featureType)) === null || _a === void 0 ? void 0 : _a.locationName) || locationName, onClick: () => handleSelectedActivityChange(id) }));
                })))));
    }
    return (React.createElement(DesktopActivitySlideWrapper, null,
        showRawMaterialFilter && (React.createElement(RawMaterialFilter, { variant: isBatchTracingActive ? 'primary' : 'default', hideAllSelection: isBatchTracingActive, items: allRawMaterials, highlightedIds: allSelectedStepActivityRawMaterialIds, selectedId: selectedRawMaterialId, onSelectionChange: handleSelectedRawMaterialChange })),
        activeStepIndex === 0 && React.createElement(SummaryChip, { summary: summary }),
        React.createElement(FlexBox, { mb: 2 }),
        !!(activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities) && (React.createElement(ActivityContainer, { activeStepIndex: activeStepIndex, activeChainActivity: activeStepActivity, chainStepActivities: (activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities) || [], setActiveNode: handleClickNode, onImpactClaimClick: onImpactClaimClick, onActivitySitesClick: (activeSiteId) => {
                onActivitySitesClick();
                onActiveSiteChange(activeSiteId);
            } })),
        isBatchTracingActive ? (React.createElement(ButtonContainer, { marginTop: (activeStep === null || activeStep === void 0 ? void 0 : activeStep.chainStepActivities.length) >= 3
                ? 50
                : activeStep.chainStepActivities.length === 2
                    ? 30
                    : 16 },
            React.createElement(ExitButton, { onClick: handleStopBatchTracingClick },
                React.createElement(Close, { size: 24 }),
                t('exit')))) : (React.createElement(React.Fragment, null))));
};
export default DesktopActivitySlide;
